import classnames from 'classnames';

import {
    ThreadType,
    type GetSessionUser_loggedInUser_sendableEmailAddresses as UserSendableEmailAddress,
} from 'nutshell-graphql-types';

import type {Recipient} from '../../types';

import {EmailMessageInput} from './email-message-input';
import {MessageInputStatusHeader} from './message-input-status-header';
import {PlainTextMessageInput} from './plain-text-message-input';
import {type SenderAddress} from './types';
import {useImageHandling} from './use-image-handling';
import {useTypingNotification} from './use-typing-notification';

import styles from './message-input.module.css';

interface Props {
    onSend: (input: {
        content: string;
        snoozeDuration?: number;
        closeThread?: boolean;
        imageUrls?: string[];
        fromAddress?: SenderAddress;
    }) => Promise<any>;
    onNoteCreate: (note: string) => Promise<any>;
    isSending: boolean;
    isComplete: boolean;
    cantSendMessage: string | null | undefined;
    shouldHideControls: boolean;
    showProgress?: boolean;
    showSnoozeOptions?: boolean;
    sessionId: string | null | undefined;
    recipient: Recipient | null | undefined;
    threadType?: ThreadType;
    sendErrorMessage?: string | null;
    signature?: string | null | undefined;
    userSendableEmailAddresses?: UserSendableEmailAddress[] | null | undefined;
}

export function MessageInput(props: Props) {
    const {showProgress = true} = props;

    const isEmailThread = Boolean(props.threadType === ThreadType.EMAIL);

    const {
        pastedImages,
        uploadedImages,
        hasImages,
        handleFilesUploaded,
        removePastedImage,
        removeUploadedImage,
        collectAllImageUrls,
        clearAllImages,
    } = useImageHandling({containerId: 'input-container', shouldDisablePaste: isEmailThread});

    const {notifyTyping} = useTypingNotification(props.sessionId);

    const shouldShowProgress = showProgress && (props.isSending || props.isComplete);
    const placeholder = hasImages ? 'Add a caption' : props.cantSendMessage || 'Type a message';

    return (
        <div
            className={classnames(styles.container, {
                [styles['has-images']]: hasImages,
            })}
        >
            <MessageInputStatusHeader
                sendErrorMessage={props.sendErrorMessage}
                shouldShowProgress={shouldShowProgress}
                isSending={props.isSending}
                isComplete={props.isComplete}
                hasImages={hasImages}
                pastedImages={pastedImages}
                uploadedImages={uploadedImages}
                onRemovePasted={removePastedImage}
                onRemoveUploaded={removeUploadedImage}
            />
            {isEmailThread ? (
                <EmailMessageInput
                    onSend={props.onSend}
                    onNoteCreate={props.onNoteCreate}
                    isSending={props.isSending}
                    showSnoozeOptions={props.showSnoozeOptions}
                    cantSendMessage={props.cantSendMessage}
                    signature={props.signature}
                    placeholder={placeholder}
                    notifyTyping={notifyTyping}
                    hasImages={hasImages}
                    collectAllImageUrls={collectAllImageUrls}
                    clearAllImages={clearAllImages}
                    userSendableEmailAddresses={props.userSendableEmailAddresses}
                />
            ) : (
                <PlainTextMessageInput
                    onSend={props.onSend}
                    isSending={props.isSending}
                    showSnoozeOptions={props.showSnoozeOptions}
                    cantSendMessage={props.cantSendMessage}
                    shouldHideControls={props.shouldHideControls}
                    threadType={props.threadType}
                    recipient={props.recipient}
                    placeholder={placeholder}
                    notifyTyping={notifyTyping}
                    hasImages={hasImages}
                    collectAllImageUrls={collectAllImageUrls}
                    clearAllImages={clearAllImages}
                    handleFilesUploaded={handleFilesUploaded}
                />
            )}
        </div>
    );
}
